/**
 * LocalStorage Checker Utility
 * 
 * Comprehensive localStorage validation and checking system
 * Runs on every page reload to ensure data integrity
 */

// No external dependencies needed

// Prevent duplicate initialization
let isInitialized = false;
let lastCheckTime = null;
let cachedHealthResult = null;
const CACHE_DURATION = 30000; // 30 seconds cache

// Required localStorage keys and their validation functions
const REQUIRED_KEYS = {
    'access_token': {
        required: true,
        validator: (value) => typeof value === 'string' && value.length > 0,
        errorMessage: 'Access token is missing or invalid'
    },
    'user_data': {
        required: true,
        validator: (value) => {
            try {
                const parsed = JSON.parse(value);
                return parsed && typeof parsed === 'object' && (parsed.id || parsed.user_id);
            } catch {
                return false;
            }
        },
        errorMessage: 'User data is missing or corrupted'
    },
};

// Optional localStorage keys with validation
const OPTIONAL_KEYS = {
    'refresh_token': {
        validator: (value) => typeof value === 'string' && value.length > 0,
        errorMessage: 'Refresh token format is invalid'
    },
    'chat_badge_count': {
        validator: (value) => !isNaN(parseInt(value)) && parseInt(value) >= 0,
        errorMessage: 'Chat badge count is invalid'
    },
    'medical_chat_badge_count': {
        validator: (value) => !isNaN(parseInt(value)) && parseInt(value) >= 0,
        errorMessage: 'Medical chat badge count is invalid'
    },
    'reaction_badge_count': {
        validator: (value) => !isNaN(parseInt(value)) && parseInt(value) >= 0,
        errorMessage: 'Reaction badge count is invalid'
    },
    'pendingMessages': {
        validator: (value) => {
            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed);
            } catch {
                return false;
            }
        },
        errorMessage: 'Pending messages format is invalid'
    }
};

// FCM token functions removed - using Reverb for notifications instead

/**
 * Check localStorage integrity on page reload
 * @returns {Promise<Object>} Check results with status and details
 */
export const checkLocalStorageOnReload = async () => {
    const results = {
        isValid: true,
        errors: [],
        warnings: [],
        checkedKeys: [],
        missingKeys: [],
        corruptedKeys: []
    };

    // Check required keys
    for (const [key, config] of Object.entries(REQUIRED_KEYS)) {
        results.checkedKeys.push(key);

        try {
            const value = localStorage.getItem(key);
            if (!value || value === 'undefined' || value === 'null') {
                // Skip fcmToken - no longer used with Reverb
                if (key === 'fcmToken') {
                    continue;
                }
                
                results.missingKeys.push(key);
                results.errors.push(`${key}: ${config.errorMessage}`);
                results.isValid = false;
            } else if (!config.validator(value)) {
                results.corruptedKeys.push(key);
                results.errors.push(`${key}: ${config.errorMessage}`);
                results.isValid = false;
            } else {
            }
        } catch (error) {
            results.errors.push(`${key}: Error checking key - ${error.message}`);
            results.isValid = false;
        }
    }

    // Check optional keys
    Object.entries(OPTIONAL_KEYS).forEach(([key, config]) => {
        const value = localStorage.getItem(key);

        if (value && value !== 'undefined' && value !== 'null') {
            results.checkedKeys.push(key);

            if (!config.validator(value)) {
                results.warnings.push(`${key}: ${config.errorMessage}`);
            } else {
            }
        }
    });

    // Check for unexpected keys (not in our defined list)
    const allDefinedKeys = [...Object.keys(REQUIRED_KEYS), ...Object.keys(OPTIONAL_KEYS)];
    const allStoredKeys = Object.keys(localStorage);
    const unexpectedKeys = allStoredKeys.filter(key => !allDefinedKeys.includes(key));

    if (unexpectedKeys.length > 0) {
        results.warnings.push(`Unexpected keys found: ${unexpectedKeys.join(', ')}`);
    }

    return results;
};

/**
 * Clean up corrupted localStorage data
 * @param {Object} checkResults - Results from checkLocalStorageOnReload
 */
export const cleanupCorruptedData = (checkResults) => {

    let cleanedCount = 0;

    // Remove corrupted keys
    checkResults.corruptedKeys.forEach(key => {
        try {
            localStorage.removeItem(key);
            cleanedCount++;
        } catch (error) {
            console.error(`❌ Error removing corrupted key ${key}:`, error);
        }
    });

    // Clean up invalid optional keys
    checkResults.warnings.forEach(warning => {
        const keyMatch = warning.match(/^([^:]+):/);
        if (keyMatch) {
            const key = keyMatch[1];
            if (OPTIONAL_KEYS[key]) {
                try {
                    localStorage.removeItem(key);
                    cleanedCount++;
                } catch (error) {
                    console.error(`❌ Error removing invalid key ${key}:`, error);
                }
            }
        }
    });

    return cleanedCount;
};

/**
 * Validate user data structure
 * @param {Object} userData - Parsed user data object
 * @returns {Object} Validation results
 */
export const validateUserData = (userData) => {
    const validation = {
        isValid: true,
        errors: [],
        warnings: []
    };

    // Required fields
    const requiredFields = ['id', 'name', 'email', 'user_type'];
    requiredFields.forEach(field => {
        if (!userData[field]) {
            validation.errors.push(`Missing required field: ${field}`);
            validation.isValid = false;
        }
    });

    // Validate email format
    if (userData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
        validation.errors.push('Invalid email format');
        validation.isValid = false;
    }

    // Validate user type
    const validUserTypes = ['PET_GUARDIAN', 'VETERINARIAN', 'SERVICE_PROVIDER', 'CLINIC_MODERATOR'];
    if (userData.user_type && !validUserTypes.includes(userData.user_type)) {
        validation.errors.push('Invalid user type');
        validation.isValid = false;
    }

    // Check for deprecated fields
    if (userData.userType && userData.user_type) {
        validation.warnings.push('Both userType and user_type found - using user_type');
    }

    return validation;
};

/**
 * Initialize localStorage monitoring
 * Runs on every page reload and sets up monitoring
 */
export const initializeLocalStorageMonitoring = async () => {
    // Prevent duplicate initialization
    if (isInitialized) {
        return { isValid: true, errors: [], warnings: [], checkedKeys: [], missingKeys: [], corruptedKeys: [] };
    }

    isInitialized = true;

    // Run initial check
    const checkResults = await checkLocalStorageOnReload();

    // If there are corrupted keys, clean them up
    if (checkResults.corruptedKeys.length > 0) {
        cleanupCorruptedData(checkResults);
    }

    // Set up storage event listener for real-time monitoring
    window.addEventListener('storage', (event) => {
        if (event.storageArea === localStorage) {
            // Validate the changed key if it's one we care about
            const allKeys = { ...REQUIRED_KEYS, ...OPTIONAL_KEYS };
            if (allKeys[event.key]) {
                const config = allKeys[event.key];
                if (!config.validator(event.newValue)) {
                    console.warn(`⚠️ Invalid value set for ${event.key}`);
                }
            }
        }
    });

    // Set up beforeunload event to check localStorage before page unload
    window.addEventListener('beforeunload', async () => {
        const finalCheck = await checkLocalStorageOnReload();
        if (!finalCheck.isValid) {
            console.warn('⚠️ localStorage issues detected on page unload');
        }
    });

    return checkResults;
};

/**
 * Get localStorage health status
 * @returns {Promise<Object>} Health status summary
 */
export const getLocalStorageHealth = async () => {
    // Check if we have a cached result that's still valid
    const now = Date.now();
    if (cachedHealthResult && lastCheckTime && (now - lastCheckTime) < CACHE_DURATION) {
        return cachedHealthResult;
    }

    const checkResults = await checkLocalStorageOnReload();

    const healthResult = {
        status: checkResults.isValid ? 'healthy' : 'unhealthy',
        totalKeys: checkResults.checkedKeys.length,
        missingKeys: checkResults.missingKeys.length,
        corruptedKeys: checkResults.corruptedKeys.length,
        warnings: checkResults.warnings.length,
        errors: checkResults.errors.length,
        lastChecked: new Date().toISOString()
    };

    // Cache the result
    cachedHealthResult = healthResult;
    lastCheckTime = now;

    return healthResult;
};

// FCM retry functions removed

/**
 * Reset initialization flag (useful for testing or manual reset)
 */
export const resetInitialization = () => {
    isInitialized = false;
};

/**
 * Clear health cache (useful for forcing fresh checks)
 */
export const clearHealthCache = () => {
    cachedHealthResult = null;
    lastCheckTime = null;
};

// FCM retry status function removed

export default {
    checkLocalStorageOnReload,
    cleanupCorruptedData,
    validateUserData,
    initializeLocalStorageMonitoring,
    getLocalStorageHealth,
    resetInitialization,
    clearHealthCache
};
